import { FC, useRef, useState, useEffect, useCallback } from 'react';
import { Table, Pagination, notification, Tooltip, Tag, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import InpService from '../../domain/ModelFile/InpService';
import SelectLocation from '@/basicsComponents/Selects/SelectLocation';
import { Modal, Search, Button, Toggle, Icon } from '@hydrodigital/design-system';

const inpService = new InpService();
interface DataType {
	enabled: boolean | undefined;
	key: string;
	inpId: string;
	inpName: number;
	mmsId: any;
	locationName: string;
	createdBy: string;
	createdAt: string;
	modelType: string;
	engineType: string;
	datResponseDTOList: {
		datId: number;
		datName: string;
		pluviometers: string;
		modifiedBy: string;
		createdAt: string;
		modifiedAt: string;
	}[];
	uploadDate: string;
	children?: DataType[];
	hidden: string;
	status: string;
	statusMessage: string;
	baselineName: string | null;
}

interface DataPluviometer {
	datId: number;
	datName: string;
	pluviometers: string;
	createdBy: string;
	modifiedBy: string;
	createdAt: string;
	modifiedAt: string;
}

const ModelTable: FC<{
	handleUpdateModelFiles: any;
	files: Array<any>;
	onChangePagination: any;
	pagination: any;
	setInputSearch: Function;
	setType: Function;
	setModelFiles: Function;
	selectedLocation: any;
	setSelectedLocation: Function;
}> = ({
	handleUpdateModelFiles,
	files,
	onChangePagination,
	pagination,
	setInputSearch,
	setModelFiles,
	selectedLocation,
	setSelectedLocation,
}) => {
	const navigate = useNavigate();
	const userId = localStorage.getItem('userId') || '';

	const selectLocationRef = useRef<any>(null);
	const baselineFileInputRef = useRef<HTMLInputElement | null>(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [modelSelect, setModelSelect] = useState<any | null>(null);
	const [showDeleteBaselineModal, setShowDeleteBaselineModal] = useState(false);
	const [baselineTargetInpId, setBaselineTargetInpId] = useState<string | null>(null);
	const [baselineDeleteTarget, setBaselineDeleteTarget] = useState<{
		inpId: string;
		baselineName: string;
	} | null>(null);
	const [uploadingBaseline, setUploadingBaseline] = useState(false);

	const blobToBase64 = (blob: Blob): Promise<string> =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(blob);
			reader.onload = () => {
				const result = reader.result as string;
				resolve(result ? result.split(',')[1] : '');
			};
			reader.onerror = (error) => reject(error);
		});

	const triggerBaselineUpload = (inpId: string) => {
		setBaselineTargetInpId(inpId);
		if (baselineFileInputRef.current) {
			baselineFileInputRef.current.value = '';
			baselineFileInputRef.current.click();
		}
	};

	const handleBaselineFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file || !baselineTargetInpId) return;

		const isValidExtension = file.name.toLowerCase().endsWith('.inp');
		if (!isValidExtension) {
			notification.error({
				message: 'Invalid file',
				description: 'Only .inp files are allowed',
				placement: 'topRight',
			});
			event.target.value = '';
			return;
		}

		try {
			setUploadingBaseline(true);
			const inpData = await blobToBase64(file);
			await inpService.saveBaseline(baselineTargetInpId, {
				inpBaselineName: file.name,
				inpData,
			});
			notification.success({
				message: 'Baseline uploaded',
				description: 'Baseline model uploaded successfully',
				placement: 'topRight',
			});
			await handleUpdateModelFiles(true);
		} catch (error) {
			console.error(error);
			notification.error({
				message: 'Upload failed',
				description: 'Could not upload baseline model',
				placement: 'topRight',
			});
		} finally {
			setUploadingBaseline(false);
			setBaselineTargetInpId(null);
			event.target.value = '';
		}
	};

	const handleDeleteBaseline = async () => {
		if (!baselineDeleteTarget) return;
		try {
			await inpService.deleteBaseline(baselineDeleteTarget.inpId);
			notification.success({
				message: 'Baseline deleted',
				description: 'Baseline model removed successfully',
				placement: 'topRight',
			});
			setShowDeleteBaselineModal(false);
			setBaselineDeleteTarget(null);
			await handleUpdateModelFiles(true);
		} catch (error) {
			console.error(error);
			notification.error({
				message: 'Delete failed',
				description: 'Could not delete baseline model',
				placement: 'topRight',
			});
		}
	};
	const expandedRowRender = (record: any) => {
		if (record.datResponseDTOList) {
		}
		return (
			<Table
				columns={columnsPluviometers}
				dataSource={record.datResponseDTOList}
				pagination={false}
				size="small"
			/>
		);
	};

	const columnsPluviometers: ColumnsType<DataPluviometer> = [
		{
			title: '',
			dataIndex: 'datName',
			key: 'datName',
			width: 550,
			render: (_, { datName }) => <span className="table-data-gis">{datName}</span>,
		},
		{
			title: '',
			dataIndex: 'locationName',
			key: 'locationName',
			width: 250,
			render: (_) => <span className="table-data-gis"></span>,
		},
		{
			title: '',
			dataIndex: 'pluviometers',
			key: 'pluviometers',
			width: 280,
			render: (_, { pluviometers }) => <span className="table-data-gis">{pluviometers}</span>,
		},
		{
			title: '',
			dataIndex: ['createdBy', 'createdAt'],
			key: 'gisFileUploadedDTOS',
			render: (_, { createdBy, createdAt }) => (
				<div>
					<span className="table-data-gis">{createdBy}</span>
					<br></br>
					<span className="date-gis">{createdAt}</span>
				</div>
			),
		},
		{
			title: '',
			dataIndex: 'datId',
			key: 'datId',
			width: 130,
			render: (_, { datId }) => <div></div>,
		},
	];

	const columns: ColumnsType<DataType> = [
		{
			title: 'File Name',
			dataIndex: 'inpName',
			key: 'inpName',
			width: 550,
			render: (_, { inpName, mmsId, inpId }) => (
				<div className="flex column">
					<span className="table-data-gis">
						{inpName} <br />
						<span style={{ color: '#023E65', fontSize: 13, fontWeight: 5 }}>ID: {inpId} </span>
					</span>
					{mmsId && (
						<span
							style={{
								color: '#2a7d97',
								textDecoration: 'underline',
								cursor: 'pointer',
							}}
							onClick={() => {
								navigate(`/?mmsMode=true&mmsId=${mmsId}&fileName=${encodeURIComponent(inpName)}`);
							}}
						>
							Provided by MMS
						</span>
					)}
				</div>
			),
		},
		{
			title: 'Location',
			dataIndex: 'locationName',
			key: 'locationName',
			width: 250,
			render: (_, { locationName }) => <span className="table-data-gis">{locationName}</span>,
		},
		{
			title: 'Rain Gauge',
			dataIndex: 'datResponseDTOList',
			key: 'datResponseDTOList',
			width: 280,
			render: (_, { datResponseDTOList }) => {
				const count = (datResponseDTOList ?? []).filter((item) =>
					item?.datName?.toLowerCase().endsWith('.dat')
				).length;
				return <span className="table-data-gis">{count} {count === 1 ? 'item' : 'items'}</span>;
			},
		},
		{
			title: 'Uploaded by',
			dataIndex: ['createdBy', 'createdAt'],
			key: 'gisFileUploadedDTOS',
			render: (_, { createdBy, createdAt }) => (
				<div>
					<span className="table-data-gis">{createdBy}</span>
					<br></br>
					<span className="date-gis">{createdAt}</span>
				</div>
			),
		},
		{
			title: 'Upload Status',
			dataIndex: 'status',
			key: 'status',
			render: (_, { status }) => {
				const fontColor = '#232B36';
				let color = '#FFEECB';
				if (status === 'COMPLETED') {
					color = '#C0EFDE';
				}
				if (status === 'IN PROGRESS') {
					color = '#FFEECB';
				}
				if (status === 'FAILED') {
					color = '#FFD3D3';
				}
				return (
					<Tag color={color} style={{ color: fontColor }}>
						{status}
					</Tag>
				);
			},
		},
		{
			title: 'Upload Status Message',
			dataIndex: 'statusMessage',
			key: 'statusMessage',
			render: (_, { statusMessage }) => <span className="table-data-gis">{statusMessage}</span>,
		},
		{
			title: 'Model Type',
			dataIndex: 'modelType',
			key: 'modelType',
			render: (_, { modelType }) => (
				<div>
					<span className="table-data-gis">{modelType}</span>
				</div>
			),
		},
		{
			title: 'Engine Type',
			dataIndex: 'engineType',
			key: 'engineType',
			render: (_, { engineType }) => (
				<div>
					<span className="table-data-gis">{engineType}</span>
				</div>
			),
		},
		{
			title: 'Baseline Model',
			dataIndex: 'baselineName',
			key: 'baselineName',
			render: (_, { baselineName, inpId, status }) => {
				const isFailed = status === 'FAILED';
				return baselineName == null ? (
					<Button
						iconName="upload"
						label="Upload"
						showIcon
						showLabel
						size="minimal"
						state={
							isFailed
								? 'disabled'
								: uploadingBaseline && baselineTargetInpId === inpId
								? 'loading'
								: 'active'
						}
						disabled={isFailed}
						loading={uploadingBaseline && baselineTargetInpId === inpId}
						variant="primary"
						IconComponent={Icon as any}
						onClick={() => {
							if (isFailed) return;
							triggerBaselineUpload(inpId);
						}}
					/>
				) : (
					<div className="flex items-center">
						<Button
							label={baselineName}
							showIcon={false}
							showLabel
							size="minimal"
							state={isFailed ? 'disabled' : 'active'}
							disabled={isFailed}
							variant="primary"
						/>
						<Icon
							name="trash-alt"
							size="s"
							style={{
								cursor: isFailed ? 'not-allowed' : 'pointer',
								marginLeft: 8,
								opacity: isFailed ? 0.5 : 1,
							}}
							onClick={() => {
								if (isFailed) return;
								setBaselineDeleteTarget({ inpId, baselineName });
								setShowDeleteBaselineModal(true);
							}}
						/>
					</div>
				);
			},
		},
		{
      title: 'Enable',
      dataIndex: 'datResponseDTOList',
      key: 'datResponseDTOList',
      render: (_, record) => {
        const isFailed = record.status === 'FAILED';
        return (
          <Toggle
            checkedText="ON"
            uncheckedText="OFF"
            size="small"
            checked={record.enabled}
            disabled={isFailed}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              if (isFailed) return;
              const checked = event.target.checked;
              try {
                if (checked) {
                  await inpService.enableInp(record.inpId, checked);
                } else {
                  await inpService.disableInp(record.inpId, checked);
                }
                const newData: any = [...files];

                setModelFiles(newData);

                await handleUpdateModelFiles(true);
              } catch (error) {
                console.error(error);
              }
            }}
          />
        );
      },
    },
		{
			title: 'Actions',
			dataIndex: 'inpId',
			key: 'inpId',
			render: (_, { inpName, inpId }) => (
				<Tooltip title="Delete">
					<DeleteOutlined
						className="mr-1"
						style={{ fontSize: '1.5rem' }}
						onClick={() => {
							setModelSelect({
								label: inpName,
								id: inpId,
							});
							setShowDeleteModal(true);
						}}
						rev={undefined}
					/>
				</Tooltip>
			),
		},
	];

	
	const fetchInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	const hasInProgressFiles = useCallback(() => {
		return files.some((file: any) => file.status === 'IN PROGRESS');
	}, [files]);

	useEffect(() => {
		
		if (fetchInterval.current) {
			clearInterval(fetchInterval.current);
			fetchInterval.current = null;
		}

		
		if (hasInProgressFiles()) {
			fetchInterval.current = setInterval(() => {
				handleUpdateModelFiles(false);
			}, 10000);
		}

		return () => {
			if (fetchInterval.current) {
				clearInterval(fetchInterval.current);
				fetchInterval.current = null;
			}
		};
	}, [hasInProgressFiles, handleUpdateModelFiles]);

	return (
		<div>
			<div className="flex justify-between">
				<div style={{ flexDirection: 'row', display: 'flex' }}>
					<Search
						size="small"
						placeholder="Search"
						onChange={(e) => {
							if (e.target.value == '') {
								setInputSearch(null);
							}
						}}
						onBlur={(e) => {
							console.log('On press');
							if (e.target) {
								const element = e.currentTarget as HTMLInputElement;
								setInputSearch(element.value);
							}
						}}
						onKeyDown={(e) => {
							if (e.target) {
								const element = e.currentTarget as HTMLInputElement;
								if (e.key === 'Enter') setInputSearch(element.value);
							}
						}}
						onClear={() => {
							setInputSearch(null);
						}}
						showClearButton
					/>
					<SelectLocation
						ref={selectLocationRef}
						className="ml-3 rounded-md"
						userId={userId}
						onSelect={(value: any) => {
							setSelectedLocation(value);
						}}
					/>
				</div>
				<div>
					<Button
						size="small"
						style={{
							fontWeight: 700,
							fontSize: 14,
							backgroundColor: '#F7F9FA',
							color: '#434B56',
							border: '1px solid #C4CCD5',
						}}
						label="Upload Model File"
						className="mr-5 boton-inicio flex items-center"
						onClick={() => {
							navigate(`modelfiles`);
						}}
					/>
				</div>
			</div>
			<div className="mt-3">
				<Table
					columns={columns}
					dataSource={files}
					pagination={false}
					expandedRowRender={expandedRowRender}
					rowKey={(record) => {
						return record.inpId;
					}}
					size="small"
					
					style={{
						maxHeight: 'calc(100vh - 330px)',
						overflowX: 'auto',
					}}
				/>
				<Pagination
					style={{ marginTop: '2rem' }}
					onChange={onChangePagination}
					current={pagination.page}
					total={pagination.totalRows}
					showSizeChanger={false}
				/>
				<Modal
					isOpen={showDeleteModal}
					size="s"
					closeOnOverlayClick={true}
					closeOnEscape={true}
					showCloseButton={true}
					onClose={() => setShowDeleteModal(false)}
				>
					<Modal.Header>
						<div className="flex column items-center">
							<span
								className="mb-2"
								style={{
									color: '#232B36',
									fontSize: '1.2rem',
									fontWeight: 'bold',
								}}
							>
								Model File delete?
							</span>
						</div>
					</Modal.Header>
					<Modal.Content>
						<div className="flex column items-center">
							<Tooltip title={modelSelect?.label}>
								<span
									className="mb-3"
									style={{
										color: '#74788D',
										fontSize: '1rem',
										fontWeight: '600',
										textAlign: 'center',
										width: '100%',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										display: 'inline-block',
										maxWidth: '100%',
									}}
								>
									Are you sure to delete <br /> {modelSelect?.label} <br />?
								</span>
							</Tooltip>
							<span>
								<Alert
									style={{
										border: 'none',
										background: 'rgba(255, 211, 211, 0.5)',
									}}
									message={
										<div
											style={{
												color: '#F46A6A',
												fontWeight: 'bolder',
											}}
										>
											Warning
										</div>
									}
									description={
										<div style={{ color: '#F46A6A' }}>
											By deleting this will be wiped off the map
										</div>
									}
									type="error"
								/>
							</span>
						</div>
					</Modal.Content>

					<div className="separator mb-2 mt-2" />
					<Modal.Footer>
						<div className="flex">
							<div className="mr-3">
								<Button
									label="No"
									showIcon={false}
									style={{
										width: '85px',
										height: '40px',
										backgroundColor: '#F7F9FA',
										color: '#434B56',
										border: '1px solid #C4CCD5',
									}}
									onClick={() => setShowDeleteModal(false)}
								/>
							</div>

							<Button
								label="Yes"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#00203C',
									color: 'white',
								}}
								onClick={async () => {
									console.log('modelSelect', modelSelect);
									if (modelSelect && modelSelect.id) {
										await inpService.deleteInps(modelSelect?.id?.toString());
										notification.success({
											message: 'Successful deletion',
											description: 'Model file deleted successfully',
											placement: 'topRight',
										});
										setShowDeleteModal(false);
										await handleUpdateModelFiles(true);
									}
								}}
							/>
						</div>
					</Modal.Footer>
				</Modal>
				<Modal
					isOpen={showDeleteBaselineModal}
					size="s"
					closeOnOverlayClick={true}
					closeOnEscape={true}
					showCloseButton={true}
					onClose={() => {
						setShowDeleteBaselineModal(false);
						setBaselineDeleteTarget(null);
					}}
				>
					<Modal.Header>
						<div className="flex column items-center">
							<span
								className="mb-2"
								style={{
									color: '#232B36',
									fontSize: '1.2rem',
									fontWeight: 'bold',
								}}
							>
								Delete baseline model?
							</span>
						</div>
					</Modal.Header>
					<Modal.Content>
						<div className="flex column items-center">
							<span
								className="mb-3"
								style={{
									color: '#74788D',
									fontSize: '1rem',
									fontWeight: '600',
									textAlign: 'center',
									width: '100%',
								}}
							>
								Are you sure to delete <br /> {baselineDeleteTarget?.baselineName} <br />?
							</span>
						</div>
					</Modal.Content>
					<div className="separator mb-2 mt-2" />
					<Modal.Footer>
						<div className="flex">
							<div className="mr-3">
								<Button
									label="No"
									showIcon={false}
									style={{
										width: '85px',
										height: '40px',
										backgroundColor: '#F7F9FA',
										color: '#434B56',
										border: '1px solid #C4CCD5',
									}}
									onClick={() => {
										setShowDeleteBaselineModal(false);
										setBaselineDeleteTarget(null);
									}}
								/>
							</div>
							<Button
								label="Yes"
								showIcon={false}
								style={{
									width: '85px',
									height: '40px',
									backgroundColor: '#00203C',
									color: 'white',
								}}
								onClick={handleDeleteBaseline}
							/>
						</div>
					</Modal.Footer>
				</Modal>
				<input
					ref={baselineFileInputRef}
					type="file"
					accept=".inp"
					style={{ display: 'none' }}
					onChange={handleBaselineFileChange}
				/>
			</div>
		</div>
	);
};

export default ModelTable;
